export type DirectiveEnvelopePart =
  | { type: "text"; text: string }
  | { type: "directive"; name: string; content?: string };

export type DirectiveMessageSegment =
  | { type: "text"; text: string }
  | { type: "directive"; name: string; content: string | null };

const DIRECTIVE_ENVELOPE_TAG = "directive-envelope";
const DIRECTIVE_ENVELOPE_VERSION = "2";
const DIRECTIVE_TAG = "directive";

// The aiserver only parses a message as XML when it starts with this exact
// prefix; anything else is plain text, escapes and all.
const DIRECTIVE_ENVELOPE_PREFIX = `<${DIRECTIVE_ENVELOPE_TAG} `;

const XML_ESCAPES: Record<string, string> = {
  "&": "&amp;",
  "<": "&lt;",
  ">": "&gt;",
  '"': "&quot;",
  "'": "&apos;",
};

export const escapeDirectiveXml = (text: string): string =>
  text.replace(/[&<>"']/g, (character) => XML_ESCAPES[character]);

// A message without directives must stay byte-identical to what the composer
// held: wrapping it would force the aiserver to XML-parse text the user never
// escaped.
export const buildDirectiveEnvelope = (
  parts: DirectiveEnvelopePart[]
): string => {
  if (!parts.some((part) => part.type === "directive")) {
    return parts
      .map((part) => (part.type === "text" ? part.text : ""))
      .join("");
  }

  const body = parts
    .map((part) => {
      if (part.type === "text") return escapeDirectiveXml(part.text);

      const name = escapeDirectiveXml(part.name);

      return part.content
        ? `<${DIRECTIVE_TAG} name="${name}">${escapeDirectiveXml(part.content)}</${DIRECTIVE_TAG}>`
        : `<${DIRECTIVE_TAG} name="${name}" />`;
    })
    .join("");

  return `<${DIRECTIVE_ENVELOPE_TAG} version="${DIRECTIVE_ENVELOPE_VERSION}">\n${body}\n</${DIRECTIVE_ENVELOPE_TAG}>`;
};

const toMessageSegments = (node: ChildNode): DirectiveMessageSegment[] => {
  if (
    node.nodeType === Node.TEXT_NODE ||
    node.nodeType === Node.CDATA_SECTION_NODE
  ) {
    const text = (node.nodeValue ?? "").trim();

    return text ? [{ type: "text", text }] : [];
  }

  if (node.nodeType !== Node.ELEMENT_NODE) return [];

  const element = node as Element;

  if (element.tagName === DIRECTIVE_TAG) {
    const content = (element.textContent ?? "").trim();

    return [
      {
        type: "directive",
        name: element.getAttribute("name") ?? "",
        content: content || null,
      },
    ];
  }

  // Not something we emit, but round-tripping it as text beats dropping
  // content the user (or a future sender) put in the envelope.
  return [
    { type: "text", text: new XMLSerializer().serializeToString(element) },
  ];
};

export const parseDirectiveEnvelope = (
  text: string
): DirectiveMessageSegment[] => {
  const asPlainText: DirectiveMessageSegment[] = [{ type: "text", text }];

  if (!text.trimStart().startsWith(DIRECTIVE_ENVELOPE_PREFIX)) {
    return asPlainText;
  }

  const parsed = new DOMParser().parseFromString(
    text.trim(),
    "application/xml"
  );

  // A message the user typed themselves can look like an envelope without
  // being valid XML; the aiserver treats that as plain text, so we do too.
  if (
    parsed.getElementsByTagName("parsererror").length > 0 ||
    parsed.documentElement.tagName !== DIRECTIVE_ENVELOPE_TAG
  ) {
    return asPlainText;
  }

  // Direct children only: directives cannot nest, so a directive inside a
  // directive is part of the outer one's text rather than a segment of its own.
  return Array.from(parsed.documentElement.childNodes).flatMap(
    toMessageSegments
  );
};

export const getDirectiveEnvelopePlainText = (text: string): string =>
  parseDirectiveEnvelope(text)
    .map((segment) =>
      segment.type === "text"
        ? segment.text
        : (segment.content ?? `/${segment.name}`)
    )
    .join("\n");
